import type { ChatMode, ChatThread } from './chat-api';

/*
 * Où mène un fil, selon son mode.
 *
 * La liste envoyait TOUS les fils vers `/c/<id>` alors que le mode est connu,
 * affiché à l'écran, et que plusieurs modes ont leur page dédiée : un fil
 * d'assistant enseignant atterrissait dans le chat générique.
 *
 * DANGEREUX SI LA TABLE CESSE D'ÊTRE EXHAUSTIVE — c'est un `Record<ChatMode, …>`
 * pour la même raison que `CHAT_MODE_ICON` : `ChatMode` est dérivé du domaine,
 * donc ajouter un mode CASSE LA COMPILATION ici tant que sa destination n'est
 * pas décidée. Sans ça le prochain mode reviendrait silencieusement sur `/c/`.
 */

/** Fil minimal : ce dont la destination a besoin, et rien de plus. */
export type RoutableThread = Pick<ChatThread, 'id' | 'mode'>;

/*
 * `null` = ce mode n'a pas de destination calculable depuis une liste générique.
 * Ce n'est pas un oubli : un fil `student` s'ouvre sous le compte du responsable
 * et sa route porte l'identifiant de l'ENFANT, que le résumé de fil ne contient
 * pas. La page des conversations de l'élève fournit donc sa propre cible, et le
 * lien parent↔enfant y est vérifié — router un fil élève vers `/c/` ouvrirait sa
 * conversation dans la surface d'équipe, dont l'autorisation ne regarde que le
 * propriétaire du fil, c'est-à-dire le parent.
 */
const ROUTE_BY_MODE: Record<ChatMode, (t: RoutableThread) => string | null> = {
  /* Le chat générique EST leur page : ce n'est pas un défaut, c'est leur home. */
  general: (t) => `/c/${t.id}`,
  parcours: (t) => `/c/${t.id}`,
  voice: (t) => `/c/${t.id}`,
  cours: (t) => `/c/${t.id}`,
  /* Ces deux-là lisent déjà `?c=<id>` pour rouvrir un fil précis. */
  teacher: (t) => `/teacher/chat?c=${t.id}`,
  admin: (t) => `/admin/chat?c=${t.id}`,
  student: () => null,
};

/** Destination d'un fil, ou `null` si l'appelant doit en fournir une. */
export function threadRoute(thread: RoutableThread): string | null {
  return ROUTE_BY_MODE[thread.mode]?.(thread) ?? null;
}
